import { useEffect, useRef, useState } from "react";
import type { JSX, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { Checkbox, Input, PopoverTrigger, Tooltip } from "@fluentui/react-components";
import { AdaptivePopover as Popover, AdaptivePopoverSurface as PopoverSurface, useOverlaySheet } from './adaptive-popover';
import { AddRegular, ArrowCounterclockwiseRegular, CheckmarkRegular, DismissRegular, DragRegular, FilterRegular, SearchRegular } from "@fluentui/react-icons";
import { clampOverlayDelta } from './overlay-geometry';
import { RESOURCE_GROUPS, resourceGroupForCharacter } from "@minglun/domain";
import type { AttributeGain, Character, ResourceGroup, Workbench } from "@minglun/domain";
import { type ResultRankOrder } from "../result-filters";
import { type ResizeDirection, resourceGroupLabels, type FilterOption } from "../workbench-shared";

export function DraggableFilterPanel({
  title,
  summary,
  canReset,
  onReset,
  onClose,
  className = "",
  children,
}: {
  title: string;
  summary: string;
  canReset: boolean;
  onReset(): void;
  onClose(): void;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const sheet = useOverlaySheet();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [panelSize, setPanelSize] = useState<{
    width: number;
    height: number;
  }>();
  const [layoutDirty, setLayoutDirty] = useState(false);
  const resizeDirections: ResizeDirection[] = [
    "n",
    "ne",
    "e",
    "se",
    "s",
    "sw",
    "w",
    "nw",
  ];
  const resizeCursors: Record<ResizeDirection, string> = {
    n: "ns-resize",
    ne: "nesw-resize",
    e: "ew-resize",
    se: "nwse-resize",
    s: "ns-resize",
    sw: "nesw-resize",
    w: "ew-resize",
    nw: "nwse-resize",
  };

  useEffect(() => () => resizeCleanupRef.current?.(), []);
  useEffect(() => { if (sheet) { resizeCleanupRef.current?.(); setOffset({ x: 0, y: 0 }); setPanelSize(undefined); } }, [sheet]);

  const resetPanel = (): void => {
    onReset();
    setPanelSize(undefined);
    setOffset({ x: 0, y: 0 });
    setLayoutDirty(false);
  };

  const startDrag = (event: ReactPointerEvent<HTMLElement>): void => {
    if (sheet || event.button !== 0 || (event.target as HTMLElement).closest('button')) return;
    const panel = surfaceRef.current;
    if (!panel) return;
    event.preventDefault();
    resizeCleanupRef.current?.();
    const rect = panel.getBoundingClientRect();
    const start = { x: event.clientX, y: event.clientY, offset };
    const move = (next: PointerEvent) => {
      if (next.pointerId !== event.pointerId) return;
      const delta = clampOverlayDelta(rect, next.clientX - start.x, next.clientY - start.y, innerWidth, innerHeight);
      setOffset({ x: start.offset.x + delta.x, y: start.offset.y + delta.y });
      setLayoutDirty(true);
    };
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    const finish = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId);
      resizeCleanupRef.current = null;
    };
    resizeCleanupRef.current = finish;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  };

  const startResize = (
    direction: ResizeDirection,
    event: ReactPointerEvent<HTMLDivElement>,
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    const panel = panelRef.current;
    if (!panel) return;

    resizeCleanupRef.current?.();
    const rect = surfaceRef.current!.getBoundingClientRect();
    // Do not parse CSS percentages as pixels (max-width:100% is not 100px).
    const minWidth = Math.min(260, window.innerWidth - 24);
    const minHeight = Math.min(120, window.innerHeight - 24);
    const cssMaxWidth = window.innerWidth - 24;
    const cssMaxHeight = Math.min(540, window.innerHeight - 24);
    const viewportInset = 8;
    const availableWidth = direction.includes("w")
      ? rect.right - viewportInset
      : direction.includes("e")
        ? window.innerWidth - rect.left - viewportInset
        : cssMaxWidth;
    const availableHeight = direction.includes("n")
      ? rect.bottom - viewportInset
      : direction.includes("s")
        ? window.innerHeight - rect.top - viewportInset
        : cssMaxHeight;
    const maxWidth = Math.max(minWidth, Math.min(cssMaxWidth, availableWidth));
    const maxHeight = Math.max(
      minHeight,
      Math.min(cssMaxHeight, availableHeight),
    );
    const startPointerX = event.clientX;
    const startPointerY = event.clientY;
    const startWidth = rect.width;
    const startHeight = rect.height;
    const startX = offset.x;
    const startY = offset.y;
    const target = event.currentTarget;
    const pointerId = event.pointerId;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    const clamp = (value: number, minimum: number, maximum: number): number =>
      Math.min(maximum, Math.max(minimum, value));

    target.setPointerCapture(pointerId);
    document.body.style.cursor = resizeCursors[direction];
    document.body.style.userSelect = "none";

    const move = (moveEvent: PointerEvent): void => {
      moveEvent.preventDefault();
      const deltaX = moveEvent.clientX - startPointerX;
      const deltaY = moveEvent.clientY - startPointerY;
      let width = startWidth;
      let height = startHeight;

      if (direction.includes("e"))
        width = clamp(startWidth + deltaX, minWidth, maxWidth);
      if (direction.includes("w"))
        width = clamp(startWidth - deltaX, minWidth, maxWidth);
      if (direction.includes("s"))
        height = clamp(startHeight + deltaY, minHeight, maxHeight);
      if (direction.includes("n"))
        height = clamp(startHeight - deltaY, minHeight, maxHeight);

      setOffset({ x: direction.includes("w") ? startX + startWidth - width : startX,
        y: direction.includes("n") ? startY + startHeight - height : startY });
      setPanelSize({ width, height });
      setLayoutDirty(true);
    };
    const finish = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      if (target.hasPointerCapture(pointerId))
        target.releasePointerCapture(pointerId);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      resizeCleanupRef.current = null;
    };

    resizeCleanupRef.current = finish;
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  };

  return (
    <PopoverSurface ref={surfaceRef} className={`result-filter-host ${className.includes('result-partner-surface') ? 'wide-filter-host' : ''}`} aria-label={title}
      style={sheet ? {} : { translate: `${offset.x}px ${offset.y}px`, ...panelSize }}>
      <section
        ref={panelRef}
        className={"result-filter-surface " + className}
        style={!sheet && panelSize ? { height: panelSize.height } : undefined}
      >
        <header
          className="result-filter-surface-header"
          onPointerDown={startDrag}
        >
          <div className="result-filter-heading">
            <DragRegular />
            <div>
              <strong>{title}</strong>
              <small>{summary}</small>
            </div>
          </div>
          <div className="result-filter-header-actions">
            <Tooltip content="恢复默认" relationship="label">
              <button
                type="button"
                disabled={!canReset && !layoutDirty}
                aria-label={"恢复" + title + "默认状态"}
                onClick={resetPanel}
              >
                <ArrowCounterclockwiseRegular />
              </button>
            </Tooltip>
            <Tooltip content="关闭" relationship="label">
              <button
                type="button"
                aria-label={"关闭" + title}
                onClick={onClose}
              >
                <DismissRegular />
              </button>
            </Tooltip>
          </div>
        </header>
        {children}
        {!sheet && resizeDirections.map((direction) => (
          <div
            key={direction}
            className={"result-filter-resize-handle " + direction}
            aria-hidden="true"
            onPointerDown={(event) => startResize(direction, event)}
          />
        ))}
      </section>
    </PopoverSurface>
  );
}

export const resultFilterPositioning = (align: "start" | "end") =>
  ({
    position: "below",
    align,
    pinned: true,
    strategy: "fixed",
    offset: 6,
  }) as const;

export function nearestScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    if (
      /(auto|scroll)/.test(style.overflowY) &&
      current.scrollHeight > current.clientHeight
    )
      return current;
    current = current.parentElement;
  }
  return null;
}

export function toggleResultFilterBelow(
  trigger: HTMLElement,
  isOpen: boolean,
  setOpen: (open: boolean) => void,
  preferredHeight = 220,
): void {
  if (isOpen) {
    setOpen(false);
    return;
  }

  // Insufficient space is handled by the adaptive sheet, without jumping the table.
  setOpen(true);
}

export function ResultTableMultiFilter({
  label,
  options,
  selectedValues,
  onChange,
}: {
  label: string;
  options: FilterOption[];
  selectedValues: string[];
  onChange(values: string[]): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const isActive = selectedValues.length > 0;
  const toggle = (value: string, checked: boolean): void =>
    onChange(
      checked
        ? [...selectedValues, value]
        : selectedValues.filter((item) => item !== value),
    );
  return (
    <Popover
      open={open}
      onOpenChange={(_event, data) => {
        setOpen(data.open);
      }}
      positioning={resultFilterPositioning("start")}
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={"result-table-filter " + (isActive ? "active" : "")}
          aria-label={label + "筛选"}
          title={"筛选" + label}
          disabled={!options.length && !isActive}
          onClick={(event) =>
            toggleResultFilterBelow(event.currentTarget, open, setOpen)
          }
        >
          <span>{label}</span>
          {isActive && <b>{selectedValues.length}</b>}
          <span className="result-filter-glyph">
            <FilterRegular />
          </span>
        </button>
      </PopoverTrigger>
      <DraggableFilterPanel
        title={label}
        summary={isActive ? "已选 " + selectedValues.length + " 项" : "全部"}
        canReset={isActive}
        onReset={() => onChange([])}
        onClose={() => setOpen(false)}
      >
        <div
          className="result-filter-option-grid"
          role="group"
          aria-label={label + "候选"}
        >
          {options.map((option) => (
            <Checkbox
              className="result-filter-option"
              key={option.value}
              checked={selectedValues.includes(option.value)}
              label={option.label}
              onChange={(_event, data) =>
                toggle(option.value, data.checked === true)
              }
            />
          ))}
          {!options.length && (
            <span className="result-filter-empty">当前没有可用候选</span>
          )}
        </div>
      </DraggableFilterPanel>
    </Popover>
  );
}

export function ResultTableChoiceFilter({
  label,
  value,
  defaultValue,
  options,
  onChange,
}: {
  label: string;
  value: string;
  defaultValue: string;
  options: string[];
  onChange(value: string): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const isActive = value !== defaultValue;
  return (
    <Popover
      open={open}
      onOpenChange={(_event, data) => {
        setOpen(data.open);
      }}
      positioning={resultFilterPositioning("start")}
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={"result-table-filter " + (isActive ? "active" : "")}
          aria-label={label + "筛选"}
          title={"筛选" + label}
          onClick={(event) =>
            toggleResultFilterBelow(event.currentTarget, open, setOpen)
          }
        >
          <span>{label}</span>
          {isActive && <b>1</b>}
          <span className="result-filter-glyph"><FilterRegular /></span>
        </button>
      </PopoverTrigger>
      <DraggableFilterPanel
        title={label}
        summary={value}
        canReset={isActive}
        onReset={() => onChange(defaultValue)}
        onClose={() => setOpen(false)}
        className="result-progress-surface"
      >
        <div className="result-single-options" role="listbox" aria-label={label + "候选"}>
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={value === option}
              className={value === option ? "active" : ""}
              key={option}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
            >
              <span>{option}</span>
              {value === option && <CheckmarkRegular />}
            </button>
          ))}
        </div>
      </DraggableFilterPanel>
    </Popover>
  );
}

export function PartnerQualitySelector({
  partnerOptions,
  qualityOptions,
  partnerGroupById,
  selectedPartnerIds,
  selectedQualityIds,
  partnerQuery,
  onPartnerQueryChange,
  onPartnerChange,
  onQualityChange,
  pruneSelectionByQuality = true,
}: {
  partnerOptions: FilterOption[];
  qualityOptions: FilterOption[];
  partnerGroupById: Map<string, ResourceGroup>;
  selectedPartnerIds: string[];
  selectedQualityIds: string[];
  partnerQuery: string;
  onPartnerQueryChange(value: string): void;
  onPartnerChange(values: string[]): void;
  onQualityChange(values: string[]): void;
  pruneSelectionByQuality?: boolean;
}): JSX.Element {
  const visiblePartners = partnerOptions.filter(
    (option) =>
      (!selectedQualityIds.length ||
        selectedQualityIds.includes(
          partnerGroupById.get(option.value) ?? "",
        )) &&
      option.label
        .toLocaleLowerCase("zh-CN")
        .includes(partnerQuery.trim().toLocaleLowerCase("zh-CN")),
  );
  const togglePartner = (value: string, checked: boolean): void =>
    onPartnerChange(
      checked
        ? [...selectedPartnerIds, value]
        : selectedPartnerIds.filter((item) => item !== value),
    );
  const toggleQuality = (value: string, checked: boolean): void => {
    const next = checked
      ? [...selectedQualityIds, value]
      : selectedQualityIds.filter((item) => item !== value);
    onQualityChange(next);
    if (pruneSelectionByQuality && next.length)
      onPartnerChange(
        selectedPartnerIds.filter((id) =>
          next.includes(partnerGroupById.get(id) ?? ""),
        ),
      );
  };
  return (
    <div className="result-partner-picker">
      <section className="result-partner-candidates">
        <div className="result-filter-column-title">
          <strong>伙伴</strong>
          <small>{visiblePartners.length} 个候选</small>
        </div>
        <Input
          className="result-partner-search"
          size="small"
          value={partnerQuery}
          contentBefore={<SearchRegular />}
          placeholder="搜索伙伴"
          aria-label="搜索伙伴候选"
          onChange={(_event, data) => onPartnerQueryChange(data.value)}
        />
        <div className="result-partner-list" role="group" aria-label="伙伴候选">
          {visiblePartners.map((option) => {
            const group = partnerGroupById.get(option.value);
            return (
              <Checkbox
                className="result-filter-option partner-option"
                key={option.value}
                checked={selectedPartnerIds.includes(option.value)}
                label={
                  <span className="result-partner-option-label">
                    <span>{option.label}</span>
                    {group && <small>{resourceGroupLabels[group]}</small>}
                  </span>
                }
                onChange={(_event, data) =>
                  togglePartner(option.value, data.checked === true)
                }
              />
            );
          })}
          {!visiblePartners.length && (
            <span className="result-filter-empty">没有匹配伙伴</span>
          )}
        </div>
      </section>
      <section className="result-quality-candidates">
        <div className="result-filter-column-title">
          <strong>品质</strong>
          <small>{qualityOptions.length} 个候选</small>
        </div>
        <div className="result-quality-list" role="group" aria-label="品质候选">
          {qualityOptions.map((option) => (
            <Checkbox
              className="result-filter-option quality-option"
              key={option.value}
              checked={selectedQualityIds.includes(option.value)}
              label={option.label}
              onChange={(_event, data) =>
                toggleQuality(option.value, data.checked === true)
              }
            />
          ))}
          {!qualityOptions.length && (
            <span className="result-filter-empty">当前没有可用品质</span>
          )}
        </div>
      </section>
    </div>
  );
}

export function ResultPartnerFilter({
  partnerOptions,
  qualityOptions,
  partnerGroupById,
  selectedPartnerIds,
  selectedQualityIds,
  onPartnerChange,
  onQualityChange,
}: {
  partnerOptions: FilterOption[];
  qualityOptions: FilterOption[];
  partnerGroupById: Map<string, ResourceGroup>;
  selectedPartnerIds: string[];
  selectedQualityIds: string[];
  onPartnerChange(values: string[]): void;
  onQualityChange(values: string[]): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [partnerQuery, setPartnerQuery] = useState("");
  const selectedPartnerCount = selectedPartnerIds.length;
  const selectedQualityCount = selectedQualityIds.length;
  const hasActiveFilter = selectedPartnerCount > 0 || selectedQualityCount > 0;
  const summary = hasActiveFilter
    ? [
        selectedPartnerCount ? `${selectedPartnerCount} 位伙伴` : "",
        selectedQualityCount ? `${selectedQualityCount} 个品质` : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "全部";
  const reset = (): void => {
    onPartnerChange([]);
    onQualityChange([]);
    setPartnerQuery("");
  };
  return (
    <Popover
      open={open}
      onOpenChange={(_event, data) => {
        setOpen(data.open);
      }}
      positioning={resultFilterPositioning("start")}
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={
            "result-table-filter result-partner-filter " +
            (hasActiveFilter ? "active" : "")
          }
          aria-label={`伙伴和品质筛选，${summary}`}
          title="筛选伙伴和品质"
          disabled={
            !partnerOptions.length && !qualityOptions.length && !hasActiveFilter
          }
          onClick={(event) =>
            toggleResultFilterBelow(event.currentTarget, open, setOpen, 420)
          }
        >
          <span>伙伴</span>
          {selectedPartnerCount > 0 && <b>{selectedPartnerCount}</b>}
          <span className="result-filter-glyph">
            <FilterRegular />
          </span>
        </button>
      </PopoverTrigger>
      <DraggableFilterPanel
        title="伙伴与品质"
        summary={summary}
        canReset={hasActiveFilter || Boolean(partnerQuery)}
        onReset={reset}
        onClose={() => setOpen(false)}
        className="result-partner-surface"
      >
        <PartnerQualitySelector
          partnerOptions={partnerOptions}
          qualityOptions={qualityOptions}
          partnerGroupById={partnerGroupById}
          selectedPartnerIds={selectedPartnerIds}
          selectedQualityIds={selectedQualityIds}
          partnerQuery={partnerQuery}
          onPartnerQueryChange={setPartnerQuery}
          onPartnerChange={onPartnerChange}
          onQualityChange={onQualityChange}
        />
      </DraggableFilterPanel>
    </Popover>
  );
}

export function WheelMemberPicker({
  characters,
  selectedCharacterIds,
  isDestiny,
  onChange,
}: {
  characters: Character[];
  selectedCharacterIds: string[];
  isDestiny: boolean;
  onChange(values: string[]): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [partnerQuery, setPartnerQuery] = useState("");
  const [selectedQualityIds, setSelectedQualityIds] = useState<string[]>([]);
  const availableCharacters = [...characters]
    .filter(
      (character) =>
        character.isEnabled && (!isDestiny || character.rarity !== "UR"),
    )
    .sort((left, right) => left.order - right.order);
  const partnerOptions = availableCharacters.map((character) => ({
    value: character.id,
    label: character.name,
  }));
  const partnerGroupById = new Map(
    availableCharacters.map((character) => [
      character.id,
      resourceGroupForCharacter(character),
    ]),
  );
  const qualityOptions = RESOURCE_GROUPS.filter((group) =>
    availableCharacters.some(
      (character) => resourceGroupForCharacter(character) === group,
    ),
  ).map((group) => ({ value: group, label: resourceGroupLabels[group] }));
  const reset = (): void => {
    setPartnerQuery("");
    setSelectedQualityIds([]);
  };
  return (
    <Popover
      open={open}
      onOpenChange={(_event, data) => {
        setOpen(data.open);
      }}
      positioning={resultFilterPositioning("end")}
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className="member-add-trigger"
          aria-label="添加伙伴"
          title="添加伙伴"
          onClick={(event) =>
            toggleResultFilterBelow(event.currentTarget, open, setOpen, 420)
          }
        >
          <AddRegular />
        </button>
      </PopoverTrigger>
      <DraggableFilterPanel
        title="伙伴与品质"
        summary={
          selectedCharacterIds.length
            ? `已加入 ${selectedCharacterIds.length} 人`
            : "尚未添加"
        }
        canReset={Boolean(partnerQuery) || selectedQualityIds.length > 0}
        onReset={reset}
        onClose={() => setOpen(false)}
        className="result-partner-surface"
      >
        <PartnerQualitySelector
          partnerOptions={partnerOptions}
          qualityOptions={qualityOptions}
          partnerGroupById={partnerGroupById}
          selectedPartnerIds={selectedCharacterIds}
          selectedQualityIds={selectedQualityIds}
          partnerQuery={partnerQuery}
          onPartnerQueryChange={setPartnerQuery}
          onPartnerChange={onChange}
          onQualityChange={setSelectedQualityIds}
          pruneSelectionByQuality={false}
        />
      </DraggableFilterPanel>
    </Popover>
  );
}

export function GainAttributePicker({
  title,
  attributes,
  gains,
  defaultValueKind,
  onChange,
}: {
  title: string;
  attributes: Workbench["sourceAttributes"];
  gains: AttributeGain[];
  defaultValueKind: AttributeGain["valueKind"];
  onChange(gains: AttributeGain[]): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const orderedAttributes = [...attributes].sort(
    (left, right) => left.order - right.order,
  );
  const groups = [
    ...new Set(orderedAttributes.map((attribute) => attribute.group)),
  ];
  const selectedIds = new Set(gains.map((gain) => gain.sourceAttributeId));
  const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");
  const visibleAttributes = orderedAttributes.filter(
    (attribute) =>
      (!selectedGroups.length || selectedGroups.includes(attribute.group)) &&
      (!normalizedQuery ||
        attribute.name.toLocaleLowerCase("zh-CN").includes(normalizedQuery)),
  );
  const toggleAttribute = (
    sourceAttributeId: string,
    checked: boolean,
  ): void => {
    if (!checked) {
      onChange(
        gains.filter((gain) => gain.sourceAttributeId !== sourceAttributeId),
      );
      return;
    }
    if (selectedIds.has(sourceAttributeId)) return;
    const source = orderedAttributes.find(
      (attribute) => attribute.id === sourceAttributeId,
    );
    if (!source) return;
    const orderById = new Map(
      orderedAttributes.map((attribute) => [attribute.id, attribute.order]),
    );
    const valueKind =
      source.id === "source.fate-value" ? "flat" : defaultValueKind;
    onChange(
      [
        ...gains,
        {
          sourceAttributeId: source.id,
          valueKind,
          value: valueKind === "percent" ? 1 : source.defaultValue,
        },
      ].sort(
        (left, right) =>
          (orderById.get(left.sourceAttributeId) ?? Number.MAX_SAFE_INTEGER) -
          (orderById.get(right.sourceAttributeId) ?? Number.MAX_SAFE_INTEGER),
      ),
    );
  };
  const toggleGroup = (group: string, checked: boolean): void =>
    setSelectedGroups((current) =>
      checked ? [...current, group] : current.filter((item) => item !== group),
    );
  const reset = (): void => {
    setQuery("");
    setSelectedGroups([]);
  };
  return (
    <Popover
      open={open}
      onOpenChange={(_event, data) => {
        setOpen(data.open);
      }}
      positioning={resultFilterPositioning("end")}
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className="member-add-trigger"
          aria-label={`选择${title}词条`}
          title={`选择${title}词条`}
          onClick={(event) =>
            toggleResultFilterBelow(event.currentTarget, open, setOpen, 420)
          }
        >
          <AddRegular />
        </button>
      </PopoverTrigger>
      <DraggableFilterPanel
        title={`${title}词条`}
        summary={gains.length ? `已加入 ${gains.length} 项` : "尚未添加"}
        canReset={Boolean(query) || selectedGroups.length > 0}
        onReset={reset}
        onClose={() => setOpen(false)}
        className="result-partner-surface gain-picker-surface"
      >
        <div className="result-partner-picker gain-attribute-picker">
          <section className="result-partner-candidates">
            <div className="result-filter-column-title">
              <strong>收益词条</strong>
              <small>{visibleAttributes.length} 个候选</small>
            </div>
            <Input
              className="result-partner-search"
              size="small"
              value={query}
              contentBefore={<SearchRegular />}
              placeholder="搜索收益词条"
              aria-label="搜索收益词条候选"
              onChange={(_event, data) => setQuery(data.value)}
            />
            <div
              className="result-partner-list"
              role="group"
              aria-label={`${title}收益词条候选`}
            >
              {visibleAttributes.map((attribute) => (
                <Checkbox
                  className="result-filter-option gain-attribute-option"
                  key={attribute.id}
                  checked={selectedIds.has(attribute.id)}
                  label={attribute.name}
                  onChange={(_event, data) =>
                    toggleAttribute(attribute.id, data.checked === true)
                  }
                />
              ))}
              {!visibleAttributes.length && (
                <span className="result-filter-empty">没有匹配词条</span>
              )}
            </div>
          </section>
          <section className="result-quality-candidates">
            <div className="result-filter-column-title">
              <strong>分类</strong>
              <small>约束左侧候选</small>
            </div>
            <div
              className="result-quality-list"
              role="group"
              aria-label="收益词条分类候选"
            >
              {groups.map((group) => (
                <Checkbox
                  className="result-filter-option quality-option"
                  key={group}
                  checked={selectedGroups.includes(group)}
                  label={group}
                  onChange={(_event, data) =>
                    toggleGroup(group, data.checked === true)
                  }
                />
              ))}
            </div>
          </section>
        </div>
      </DraggableFilterPanel>
    </Popover>
  );
}

export function ResultTableSort({
  label,
  value,
  onChange,
}: {
  label: string;
  value: ResultRankOrder;
  onChange(value: ResultRankOrder): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const isActive = value !== "默认";
  const options: { value: ResultRankOrder; label: string }[] = [
    { value: "默认", label: "默认顺序" },
    { value: "升序", label: "低阶在前" },
    { value: "降序", label: "高阶在前" },
  ];
  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? "默认顺序";
  return (
    <Popover
      open={open}
      onOpenChange={(_event, data) => {
        setOpen(data.open);
      }}
      positioning={resultFilterPositioning("start")}
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={"result-table-filter " + (isActive ? "active" : "")}
          aria-label={"阶次排序：" + selectedLabel}
          title="排序阶次"
          onClick={(event) =>
            toggleResultFilterBelow(event.currentTarget, open, setOpen)
          }
        >
          <span>{label}</span>
          {value === "升序" && <b>↑</b>}
          {value === "降序" && <b>↓</b>}
          <span className="result-filter-glyph">
            <FilterRegular />
          </span>
        </button>
      </PopoverTrigger>
      <DraggableFilterPanel
        title="阶星排序"
        summary={selectedLabel}
        canReset={isActive}
        onReset={() => onChange("默认")}
        onClose={() => setOpen(false)}
        className="result-sort-surface"
      >
        <div className="result-single-options">
          {options.map((option) => (
            <button
              type="button"
              className={option.value === value ? "active" : ""}
              key={option.value}
              onClick={() => onChange(option.value)}
            >
              <span>{option.label}</span>
              {option.value === value && <CheckmarkRegular />}
            </button>
          ))}
        </div>
      </DraggableFilterPanel>
    </Popover>
  );
}

export function ResultProgressHeader({
  checked,
  disabled,
  filter,
  onToggleAll,
  onFilterChange,
}: {
  checked: boolean | "mixed";
  disabled: boolean;
  filter: string;
  onToggleAll(): void;
  onFilterChange(value: string): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const options: FilterOption[] = [
    { value: "未完成", label: "未完成" },
    { value: "已完成", label: "已完成" },
  ];
  return (
    <div className="result-progress-header">
      <Checkbox
        className="result-progress-all"
        checked={checked}
        disabled={disabled}
        aria-label={
          checked === true
            ? "当前筛选列表全部恢复为未完成"
            : "当前筛选列表全部标记为已完成"
        }
        title={checked === true ? "全部恢复为未完成" : "全部标记为已完成"}
        onChange={onToggleAll}
      />
      <Popover
        open={open}
        onOpenChange={(_event, data) => {
          setOpen(data.open);
        }}
        positioning={resultFilterPositioning("start")}
      >
        <PopoverTrigger disableButtonEnhancement>
          <button
            type="button"
            className={
              "result-progress-filter " + (filter !== "全部" ? "active" : "")
            }
            aria-label={"执行进度筛选：" + filter}
            title="筛选执行进度"
            onClick={(event) =>
              toggleResultFilterBelow(event.currentTarget, open, setOpen)
            }
          >
            <FilterRegular />
          </button>
        </PopoverTrigger>
        <DraggableFilterPanel
          title="执行进度"
          summary={filter === "全部" ? "全部" : filter}
          canReset={filter !== "全部"}
          onReset={() => onFilterChange("全部")}
          onClose={() => setOpen(false)}
          className="result-progress-surface"
        >
          <div className="result-single-options">
            {options.map((option) => (
              <button
                type="button"
                className={option.value === filter ? "active" : ""}
                key={option.value}
                onClick={() =>
                  onFilterChange(
                    option.value === filter ? "全部" : option.value,
                  )
                }
              >
                <span>{option.label}</span>
                {option.value === filter && <CheckmarkRegular />}
              </button>
            ))}
          </div>
        </DraggableFilterPanel>
      </Popover>
    </div>
  );
}

